/**
 * Embedded HLS feed for the fan viewer — moved verbatim from
 * BrowseStateView during the MVP three-mode rebuild, plus:
 *
 *   - `onVideoEdgeUpdate` plumb-through to HLSPlayer (video-end UTC,
 *     i.e. the newest playable media as PROGRAM-DATE-TIME; the caller
 *     derives lag against the newest GPS fix — lag.ts source 1)
 *   - `badge` overlay: LIVE (red, pulsing) vs REPLAY · Ln (cyan) per
 *     the mockup's .vid-state — flips when a past lap is selected.
 *
 * M21 invariant lives at the CALL SITES: every <FeedPlayer> mount is
 * keyed on its session_id so an HLSPlayer instance is never reused
 * across sessions (per-session PDT anchors would corrupt UTC mapping).
 */

import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import api from '../../services/api';
import { BufferedSession, SessionCoverage } from '../../types';
import HLSPlayer, { GlassRailData, HLSPlayerHandle } from '../HLSPlayer';
import { isSnapshotSettled } from './snapshotStatus';
import V2Player from './V2Player';

export interface VideoBadge {
  /** e.g. "LIVE", "REPLAY · L4", "REPLAY" */
  text: string;
  kind: 'live' | 'replay';
}

export const FeedPlayer: React.FC<{
  sessionId: string | null;
  playerRef?: React.MutableRefObject<HLSPlayerHandle | null>;
  onUtcUpdate?: (utcMs: number | null) => void;
  /** Stream lag of the playing camera (wallclock − playhead PDT), s. */
  onVideoEdgeUpdate?: (videoEndUtcMs: number | null) => void;
  /** Fires once the mounted v2 player's timeline anchor (first PROGRAM-
   *  DATE-TIME) is known; UTC ms. Legacy (non-v2) sessions never fire it. */
  onPdtReady?: (firstPdtMs: number) => void;
  initialSeekUtcMs?: number | null;
  /** Finished-event replay: keep the backend's is_active verdict so the
   *  player runs as VOD instead of live-tail polling for new segments. */
  vod?: boolean;
  /** LIVE / REPLAY state badge overlaid top-left (mockup .vid-state). */
  badge?: VideoBadge | null;
  /** Optional bottom-right HUD chip (e.g. live speed). */
  cornerChip?: string | null;
  /** Theater: cap width so the 16:9 tile's height fits the viewport
   *  (video scales with the browser window instead of overflowing). */
  fill?: boolean;
  /** Height-bound the video tile (object-contain letterbox) instead of
   *  the default width-driven 16:9 box. Set on the mobile-landscape stage
   *  where the player area is short and wide — a width-driven box is far
   *  too tall and overflows/scrolls. Does NOT enter browser fullscreen. */
  heightFit?: boolean;
  /** Bubbles the auto-hiding playbar's visibility (see HLSPlayer). */
  onControlsHiddenChange?: (hidden: boolean) => void;
  /** Override the Maximise button to fullscreen a parent (the landscape
   *  shell) instead of the player; `maximized` drives the button icon. */
  onMaximize?: () => void;
  maximized?: boolean;
  /** Fullscreen-landscape glass telemetry rail (handover §3.6). */
  glassRail?: GlassRailData | null;
  /** Start playback once loaded (map-full inset remounts). */
  autoPlay?: boolean;
}> = ({
  sessionId,
  playerRef,
  onUtcUpdate,
  onVideoEdgeUpdate,
  onPdtReady,
  initialSeekUtcMs,
  vod = false,
  badge = null,
  cornerChip = null,
  fill = false,
  heightFit = false,
  onControlsHiddenChange,
  onMaximize,
  maximized = false,
  glassRail = null,
  autoPlay = false,
}) => {
  const [session, setSession] = useState<BufferedSession | null>(null);
  // Orientation correction (session.video_rotation). Applied via CSS to the
  // <video> element ONLY — rotating the wrapper would flip the controls too.
  const [videoRotation, setVideoRotation] = useState<number | null>(null);
  // A5 media-contract-v2 sessions have no BufferedSession shape; a v2 coverage verdict mounts
  // the persistent V2Player instead. Unions refresh in place, and a snapshot_rev change
  // reloads the player's source in place too (see V2Player's snapshotRev prop).
  const [v2Coverage, setV2Coverage] = useState<SessionCoverage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastLoadedRef = useRef<string | null>(null);
  const v2ActiveRef = useRef(false);
  // Read inside the polling tick below without retriggering the effect that owns it.
  const v2CoverageRef = useRef<SessionCoverage | null>(null);
  // Once the backend reports the finalized snapshot complete AND the device no
  // longer active, there is nothing left to heal — stop polling regardless of
  // page mode. Cleared only on a session change; a settled VOD view must not
  // resume polling on its own.
  const v2PollStoppedRef = useRef(false);

  useEffect(() => {
    setVideoRotation(null);
    if (!sessionId) return;
    let cancelled = false;
    api
      .getSessionMetadata(sessionId)
      .then((meta) => {
        if (!cancelled) setVideoRotation(meta.video_rotation ?? null);
      })
      .catch(() => {
        /* no metadata (anon-denied / legacy) — play uncorrected */
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setSession(null);
      setV2Coverage(null);
      v2CoverageRef.current = null;
      setError(null);
      lastLoadedRef.current = null;
      v2ActiveRef.current = false;
      v2PollStoppedRef.current = false;
      return;
    }
    let cancelled = false;
    setError(null);
    setV2Coverage(null);
    v2CoverageRef.current = null;
    v2ActiveRef.current = false;
    v2PollStoppedRef.current = false;

    const applyV2Coverage = (coverage: SessionCoverage) => {
      v2CoverageRef.current = coverage;
      setV2Coverage(coverage);
      if (coverage.snapshot_complete === true && coverage.is_active === false) {
        v2PollStoppedRef.current = true;
      }
    };

    // Live v2: the unions grow (and heal) as artifacts land — refresh them in place, and
    // pick up a healed finalized snapshot (see the poll condition below for when this stops).
    const refreshV2 = async () => {
      try {
        const coverage = await api.getSessionCoverage(sessionId);
        if (!cancelled && coverage?.media_contract_version === 2) {
          applyV2Coverage(coverage);
        }
      } catch {
        /* transient — retry next tick, keep the last good coverage */
      }
    };

    const load = async () => {
      // Coverage first: it alone can identify v2 (no legacy buffered shape
      // exists for those sessions), and checking it first — rather than
      // after a legacy lookup returns null — skips a request every v2
      // session used to pay serially on every fresh mount.
      let coverage: SessionCoverage | null = null;
      try {
        coverage = await api.getSessionCoverage(sessionId);
        if (cancelled) return;
      } catch (err) {
        if (cancelled) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status !== 401 && status !== 404) {
          // Network/5xx — transient. Leave whatever is on screen (last good
          // coverage, or the pending/error state already showing) for the
          // next poll tick to retry, instead of misreading this as legacy.
          return;
        }
        // Unauthorized/not-found isn't transient — fall through to the
        // legacy lookup below, which fails the same way for the same
        // reason and produces today's error message.
      }
      if (coverage?.media_contract_version === 2) {
        v2ActiveRef.current = true;
        applyV2Coverage(coverage);
        setError(null);
        lastLoadedRef.current = sessionId;
        return;
      }
      try {
        const found = await api.getBufferedSession(sessionId);
        if (cancelled) return;
        if (!found) {
          setSession(null);
          setError('waiting for first segment…');
          return;
        }
        // Live mode forces `is_active: true` — the device is currently
        // emitting telemetry so the player should treat the feed as live
        // (live tail + append loop) rather than as a finished session.
        // Replay keeps the backend verdict so finished sessions play VOD.
        setSession(vod ? found : { ...found, is_active: true });
        setError(null);
        lastLoadedRef.current = sessionId;
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'failed to load feed');
        }
      }
    };

    void load();
    // Poll while we haven't loaded yet — a fresh recording can take a few
    // seconds to put its first segment in S3. Once v2 is active, keep
    // refreshing coverage while this is a live view or the latest snapshot
    // could still gain media — a finished, fully healed recording being
    // watched as VOD has nothing left to poll for.
    const id = window.setInterval(() => {
      if (lastLoadedRef.current !== sessionId) {
        void load();
        return;
      }
      if (
        v2ActiveRef.current &&
        !v2PollStoppedRef.current &&
        (!vod || !isSnapshotSettled(v2CoverageRef.current))
      ) {
        void refreshV2();
      }
    }, 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [sessionId, vod]);

  // Lag source 1 is "the newest playable media's PDT", which for a v2 session
  // is the ledger's own playable union — the same truth the playlists compose
  // from, already refreshed on the 5 s tick above. Reading it here keeps the
  // number playhead-independent (scrubbing back is not lag) without asking the
  // player to infer an edge from its buffer.
  useEffect(() => {
    if (!onVideoEdgeUpdate || !v2Coverage) return;
    const spans = v2Coverage.playable_union ?? v2Coverage.union ?? [];
    onVideoEdgeUpdate(spans.length ? spans[spans.length - 1][1] : null);
  }, [v2Coverage, onVideoEdgeUpdate]);

  // The persistent V2Player judged its master unrecoverable (bounded retries
  // exhausted — e.g. a 404 that never resolves). There is no legacy shape to
  // fall back to for a v2 session, so surface a clear error instead of the
  // player's own infinite-spinner failure mode. The next 5s tick's
  // `refreshV2` above will remount it if coverage still says v2 and the
  // underlying issue has cleared — a slow, visible retry, not a tight loop.
  const handleV2FatalError = (detail: string) => {
    v2ActiveRef.current = false;
    setV2Coverage(null);
    setError(`v2 playback unavailable — ${detail}`);
  };

  if (!sessionId) {
    return (
      <div className="flex h-56 items-center justify-center bg-bg-video text-xs text-fg-secondary">
        {vod ? 'no recording for this device' : 'not live right now'}
      </div>
    );
  }
  const rotationDeg =
    videoRotation === 90 || videoRotation === 180 || videoRotation === 270 ? videoRotation : null;
  const rotationAttr = rotationDeg == null ? undefined : rotationDeg % 180 === 0 ? 'half' : 'quarter';
  const rotationVar = rotationDeg == null ? undefined : ({ '--vidrot': `${rotationDeg}deg` } as React.CSSProperties);

  if (v2Coverage) {
    // A5 media-contract-v2: one persistent player for the whole session. The master URL stays
    // stable; unions and snapshotRev refresh as props, and V2Player reloads in place only when
    // snapshotRev changes.
    return (
      <div
        data-vidrot={rotationAttr}
        className={cn(
          // flex column so PlayerChrome's flex-grow media area actually takes
          // the height this box defines.
          'relative flex flex-col bg-bg-video',
          !heightFit && 'aspect-video',
          heightFit && 'h-full min-h-0',
          fill && 'mx-auto w-full',
        )}
        style={{ ...(fill ? { maxWidth: 'calc((100dvh - 150px) * 1.7778)' } : null), ...rotationVar }}
      >
        {badge && (
          <div
            className={cn(
              'feed-video-badge absolute left-2 top-2 z-10 flex items-center gap-1.5 rounded bg-black/70 px-2 py-1 text-[10px] font-bold',
              badge.kind === 'live' ? 'text-white' : 'text-accent-cyan',
            )}
          >
            <span
              className={cn(
                'inline-block h-1.5 w-1.5 rounded-full',
                badge.kind === 'live' ? 'animate-pulse bg-status-error' : 'bg-accent-cyan',
              )}
            />
            {badge.text}
          </div>
        )}
        {cornerChip && (
          <div className="absolute bottom-2 right-2 z-10 rounded bg-black/70 px-2 py-1 font-mono text-[11px] text-white">
            {cornerChip}
          </div>
        )}
        <V2Player
          ref={playerRef}
          masterUrl={`/api/fleet/sessions/${encodeURIComponent(sessionId ?? '')}/v2/master.m3u8`}
          audioUnion={v2Coverage.audio_union}
          videoUnion={v2Coverage.video_union}
          snapshotRev={v2Coverage.snapshot_rev ?? null}
          // Explicit VOD mode always wins; backend truth only ever settles a
          // LIVE page into VOD once the device stops recording — never the
          // reverse, so a caller that already committed to VOD can't be
          // pulled back to live by a stale/absent is_active.
          live={!vod && (v2Coverage.is_active ?? true)}
          initialSeekUtcMs={initialSeekUtcMs}
          autoPlay={autoPlay}
          chrome="minimal"
          heightFit={heightFit}
          glassRail={glassRail}
          onMaximize={onMaximize}
          maximized={maximized}
          onControlsHiddenChange={onControlsHiddenChange}
          onUtcUpdate={onUtcUpdate}
          onPdtReady={onPdtReady}
          onFatalError={handleV2FatalError}
        />
      </div>
    );
  }
  if (!session) {
    return (
      <div className="flex h-56 items-center justify-center bg-bg-video text-xs text-fg-secondary">
        {error ?? 'loading feed…'}
      </div>
    );
  }
  return (
    <div
      data-vidrot={rotationAttr}
      className={cn('relative bg-bg-video', fill && 'mx-auto w-full')}
      style={{ ...(fill ? { maxWidth: 'calc((100dvh - 150px) * 1.7778)' } : null), ...rotationVar }}
    >
      {badge && (
        <div
          className={cn(
            'feed-video-badge absolute left-2 top-2 z-10 flex items-center gap-1.5 rounded bg-black/70 px-2 py-1 text-[10px] font-bold',
            badge.kind === 'live' ? 'text-white' : 'text-accent-cyan',
          )}
        >
          <span
            className={cn(
              'inline-block h-1.5 w-1.5 rounded-full',
              badge.kind === 'live' ? 'animate-pulse bg-status-error' : 'bg-accent-cyan',
            )}
          />
          {badge.text}
        </div>
      )}
      {cornerChip && (
        <div className="absolute bottom-2 right-2 z-10 rounded bg-black/70 px-2 py-1 font-mono text-[11px] text-white">
          {cornerChip}
        </div>
      )}
      {/* Public viewer: minimal chrome — no session ids, clock chips
          or buffer badges (those are dashboard diagnostics). */}
      <HLSPlayer
        ref={playerRef}
        session={session}
        onUtcUpdate={onUtcUpdate}
        onVideoEdgeUpdate={onVideoEdgeUpdate}
        initialSeekUtcMs={initialSeekUtcMs}
        chrome="minimal"
        heightFit={heightFit}
        onControlsHiddenChange={onControlsHiddenChange}
        onMaximize={onMaximize}
        isMaximized={maximized}
        glassRail={glassRail}
      />
    </div>
  );
};

export default FeedPlayer;
